import { AppSettings } from "@/AppSettings";
import { ConfigurationSourceDataMap, ConfigurationStatus, CoordinatingGroup, Institution, MasterGroup, Metadata, Note, NoteStatus, NoteType, Participant, SourceData, SourceDataEventType, SourceDataFieldType, SourceDataItem, SourceDataItemGroupType, Trial } from "@/api/dtos";
import ConfigurationSourceDataContext from "@/contexts/data/ConfigurationSourceDataContext";
import CoordinatingGroupsContext from "@/contexts/data/CoordinatingGroupsContext";
import InstitutionsContext from "@/contexts/data/InstitutionsContext";
import MasterGroupsContext from "@/contexts/data/MasterGroupsContext";
import MetadataContext from "@/contexts/data/MetadataContext";
import ParticipantsContext from "@/contexts/data/ParticipantsContext";
import SourceDataContext from "@/contexts/data/SourceDataContext";
import TrialConfigurationSourceDataContext from "@/contexts/data/TrialConfigurationSourceDataContext";
import TrialsContext from "@/contexts/data/TrialsContext";
import ListViewType from "@/types/list/ListViewType";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, AlertTitle, Box, Button, Chip, Dialog, Divider, List, Menu, MenuItem, Paper, Table, TableCell, TableHead, TableRow, Theme, Tooltip, Typography, styled, useTheme } from "@mui/material";
import { immerable } from "immer";
import { FC, memo, useCallback, useContext, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { TableVirtuoso, Virtuoso } from "react-virtuoso";
import ListTableToggle from "../common/ListTableToggle";
import NoteDetails from "./NoteDetails";
import NoteView from "./NoteView";
import NotesAndQueriesListSort, { DefaultSortableFields, INotesAndQueriesListSortable, NotesAndQueriesListFilter } from "./NotesAndQueriesListSort";
import ExternalUsersContext from "@/contexts/data/ExternalUsersContext";
import useEventConfiguration from "@/hooks/form/configuration/useEventConfiguration";
import useFormConfiguration from "@/hooks/form/configuration/useFormConfiguration";
import useItemGroupConfiguration from "@/hooks/form/configuration/useItemGroupConfiguration";
import useItem from "@/hooks/form/source-data/useItem";
import { SourceDataItemMapping } from "@/components/forms/forms/source-data/values/Index";
import NoteEditForm from "@/components/forms/forms/notes/NoteEditForm";
import { StatusListItemTooltip } from "../status/StatusListStats";
import noteStatusToColor from "@/utilities/configuration/noteStatusToColor";
import { DateTime } from "luxon";
import noteStatusToText from "@/utilities/configuration/noteStatusToText";
import noteTypeToText from "@/utilities/configuration/noteTypeToText";
import { useLocalStorage } from "react-use/lib";


const Subtitle = styled(Typography)(({ theme }) => ({
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2)
}));

const FilterSortBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(-7),
    alignItems: 'stretch'
}));

const SortBox = styled(Paper)(({ theme }) => ({
    marginLeft: theme.spacing(3),
    marginBottom: theme.spacing(3),
    display: 'flex'
}));

const ViewBox = styled(Paper)(({ theme }) => ({
    marginLeft: theme.spacing(3),
    marginBottom: theme.spacing(3),
    display: 'flex'
}));

const SearchBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    minHeight: theme.spacing(6)
}));

const SearchButton = styled(Button)(({ theme }) => ({
    minWidth: theme.spacing(6),
    width: theme.spacing(6),
    alignSelf: 'stretch',
    padding: theme.spacing(0)
}));

const CountBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    minHeight: theme.spacing(6),
    marginRight: theme.spacing(2)
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const Buttons = styled(Box)(({ theme }) => ({
    // marginBottom: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'flex-end',
    padding: theme.spacing(2)

}));

const ChipBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: "column",
    gap: theme.spacing(1),
    // marginLeft: 'auto'
}));

const StatusChip = styled(Chip)(({ theme }) => ({
    height: theme.spacing(3),
    // minWidth: theme.spacing(18.75)
}));

const DateChip = styled(Chip)(({ theme }) => ({
    height: theme.spacing(3),
    // minWidth: theme.spacing(15)
}));

const UserChip = styled(Chip)(({ theme }) => ({
    height: theme.spacing(3),
    // minWidth: theme.spacing(15)
}));


const NoteTable = styled(Table)(({ theme }) => ({
    '& tr > td': {
        verticalAlign: 'top'
    },

    '& a': {
        color: theme.palette.text.primary,

        '&:hover': {
            color: theme.palette.text.primary,
        },

        '&:visited': {
            color: theme.palette.text.primary,
        }
    }
}));



interface IVirtuosoContext {
    trialId?: number;
    masterGroupId?: number;
    coordinatingGroupId?: number;
    institutionId?: number;
    participantId?: number;
    studyEventOid?: string;
    studyEventRepeat?: number;
    caseReportFormOid?: string;
    caseReportFormVersionOid?: string;
    itemGroupName?: string;
    itemGroupRepeat?: number;
    itemName?: string;

    theme: Theme;
    trials?: Record<number, Trial>;
    masterGroups?: Record<number, MasterGroup>;
    coordinatingGroups?: Record<number, CoordinatingGroup>;
    institutions?: Record<number, Institution>;
    participants?: Record<number, Participant>;
    metadata?: Metadata;
    trialConfiguration?: ConfigurationSourceDataMap;
    configuration?: ConfigurationSourceDataMap;
    sourceData?: SourceData;

    hideDetails?: boolean;
}

interface IVirtuosoInnerItem {
    index: number;
    item: Note;
    context: IVirtuosoContext;
    informationOnly?: boolean;
}



interface IVirtuosoTableInnerItem {
    index: number;
    item: Note;
    context: IVirtuosoContext;
    hideTrial?: boolean;
    hideInstitution?: boolean;
    hideParticipant?: boolean;
    hideEvent?: boolean;
    hideForm?: boolean;
    hideItemGroup?: boolean;
    hideItem?: boolean;
    hideItemValue?: boolean;
    hideNoteType?: boolean;
    hideStatus?: boolean;
    columnDefinitions: Record<string, number>
}

const useColumnDefinitions = () => {
    const theme = useTheme();

    const noteTypeSpacing = theme.spacing(25);
    const resolutionStatusSpacing = theme.spacing(22);
    const trialSpacing = theme.spacing(20);
    const institutionSpacing = theme.spacing(30);
    const participantSpacing = theme.spacing(12);
    const studyEventSpacing = theme.spacing(30);
    const caseReportFormSpacing = theme.spacing(30);
    const itemGroupSpacing = theme.spacing(30);
    const itemSpacing = theme.spacing(50);
    const itemValueSpacing = theme.spacing(30);
    const noteReasonSpacing = theme.spacing(40);
    const noteDetailsSpacing = theme.spacing(75);
    const dateCreatedSpacing = theme.spacing(14);
    const createdBySpacing = theme.spacing(20);
    const latestUpdateSpacing = theme.spacing(40);
    const latestUpdateDetailsSpacing = theme.spacing(75);
    const updatedBySpacing = theme.spacing(20);
    const dateUpdatedSpacing = theme.spacing(14);
    const actionsSpacing = theme.spacing(10);

    const columnDefinitions: Record<string, number> = useMemo(() => ({
        "Note Type": +(noteTypeSpacing.replace("px", "")),
        "Resolution Status": +(resolutionStatusSpacing.replace("px", "")),
        "Trial": +(trialSpacing.replace("px", "")),
        "Institution": +(institutionSpacing.replace("px", "")),
        "Participant": +(participantSpacing.replace("px", "")),
        "Study Event": +(studyEventSpacing.replace("px", "")),
        "Case Report Form": +(caseReportFormSpacing.replace("px", "")),
        "Item Group": +(itemGroupSpacing.replace("px", "")),
        "Item": +(itemSpacing.replace("px", "")),
        "Item Value": +(itemValueSpacing.replace("px", "")),
        "Description": +(noteReasonSpacing.replace("px", "")),
        "Detailed Notes": +(noteDetailsSpacing.replace("px", "")),
        "Date Created": +(dateCreatedSpacing.replace("px", "")),
        "Created By": +(createdBySpacing.replace("px", "")),
        "Latest Response": +(latestUpdateSpacing.replace("px", "")),
        "Latest Response Details": +(latestUpdateDetailsSpacing.replace("px", "")),
        "Updated By": +(updatedBySpacing.replace("px", "")),
        "Date Updated": +(dateUpdatedSpacing.replace("px", "")),
        "Actions": +(actionsSpacing.replace("px", ""))
    }), [
        noteTypeSpacing,
        resolutionStatusSpacing,
        trialSpacing,
        institutionSpacing,
        participantSpacing,
        studyEventSpacing,
        caseReportFormSpacing,
        itemGroupSpacing,
        itemSpacing,
        itemValueSpacing,
        noteReasonSpacing,
        noteDetailsSpacing,
        dateCreatedSpacing,
        createdBySpacing,
        latestUpdateSpacing,
        updatedBySpacing,
        dateUpdatedSpacing,
        actionsSpacing
    ]);

    return columnDefinitions;
}

const NoteTableRow = ({
    index,
    item: itemProp,
    context,
    hideTrial,
    hideInstitution,
    hideParticipant,
    hideEvent,
    hideForm,
    hideItemGroup,
    hideItem,
    hideItemValue,
    hideNoteType,
    hideStatus,
    columnDefinitions
}: IVirtuosoTableInnerItem) => {

    
    const {
        theme,

        trials,
        masterGroups,
        coordinatingGroups,
        institutions,
        participants,
        metadata,
        trialConfiguration,
        configuration,
        sourceData,
    } = context;

    const contextStatus = useMemo(() => {
        if (itemProp.participantId) {
            return participants?.[itemProp.participantId]?.status ?? ConfigurationStatus.Disabled;
        }

        if (itemProp.institutionId) {
            return institutions?.[itemProp.institutionId]?.status ?? ConfigurationStatus.Disabled;
        }

        if (itemProp.coordinatingGroupId) {
            return coordinatingGroups?.[itemProp.coordinatingGroupId]?.status ?? ConfigurationStatus.Disabled;
        }

        if (itemProp.masterGroupId) {
            return masterGroups?.[itemProp.masterGroupId]?.status ?? ConfigurationStatus.Disabled;
        }

        if (itemProp.trialId) {
            return trials?.[itemProp.trialId]?.status ?? ConfigurationStatus.Disabled;
        }
    }, [
        trials,
        masterGroups,
        coordinatingGroups,
        institutions,
        participants,

        itemProp
    ]);

    const showEdit = contextStatus == ConfigurationStatus.Enabled && !!AppSettings.allowNotesAndQueriesEdit;

    const [ editStatus, setEditStatus ] = useState<NoteStatus>();
    const [ view, setView ] = useState<boolean>(false);
    const onEditCancel = useCallback(() => {
        setEditStatus(undefined);
        setView(false);
    }, [setEditStatus, setView]);

    const trial = (itemProp.trialId ? trials?.[itemProp.trialId] : undefined);
    const masterGroup = (itemProp.masterGroupId ? masterGroups?.[itemProp.masterGroupId] : undefined);
    const coordinatingGroup = (itemProp.coordinatingGroupId ? coordinatingGroups?.[itemProp.coordinatingGroupId] : undefined);
    const institution = (itemProp.institutionId ? institutions?.[itemProp.institutionId] : undefined);
    const participant = (itemProp.participantId ? participants?.[itemProp.participantId] : undefined);

    const studyEvent = useEventConfiguration(
        metadata,
        trialConfiguration,
        configuration ?? trialConfiguration,
        trial,
        (configuration ?? trialConfiguration)?.configuration?.id,
        itemProp.studyEventOid
    );

    const caseReportForm = useFormConfiguration(
        metadata,
        trialConfiguration,
        configuration ?? trialConfiguration,
        trial,
        (configuration ?? trialConfiguration)?.configuration?.id,
        itemProp.studyEventOid,
        itemProp.caseReportFormOid
    );

    const itemGroup = useItemGroupConfiguration(
        metadata,
        trialConfiguration,
        configuration ?? trialConfiguration,
        trial,
        (configuration ?? trialConfiguration)?.configuration?.id,
        itemProp.studyEventOid,
        itemProp.caseReportFormOid,
        itemProp.caseReportFormVersionOid,
        itemProp.itemGroupName
    );

    // const item = useItemConfiguration(
    //     metadata,
    //     trialConfiguration,
    //     configuration ?? trialConfiguration,
    //     trial,
    //     (configuration ?? trialConfiguration)?.configuration?.id,
    //     studyEventOid,
    //     caseReportFormOid,
    //     caseReportFormVersionOid,
    //     itemGroupName,
    //     itemName
    // );

    const item = useItem(
        metadata,
        trialConfiguration,
        configuration ?? trialConfiguration,
        sourceData ?? new SourceData({
            date: undefined,
            events: undefined,
            forms: undefined,
            items: {
                [itemProp.studyEventOid!]: {
                    [itemProp.studyEventRepeat!]: {
                        [itemProp.caseReportFormOid!]: {
                            [itemProp.itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey]: {
                                [itemProp.itemGroupRepeat!]: {
                                    [itemProp.itemName!]: new SourceDataItem({
                                        value: itemProp.itemValue
                                    })
                                }
                            }
                        }
                    }
                }
            }
        }),
        trial,
        masterGroup,
        coordinatingGroup,
        institution,
        participant,
        itemProp.studyEventOid,
        itemProp.studyEventRepeat,
        itemProp.caseReportFormOid,
        itemProp.caseReportFormVersionOid,
        itemProp.itemGroupName,
        itemProp.itemGroupRepeat,
        itemProp.itemName
    )

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = useMemo(() => !!anchorEl, [anchorEl]);

    const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    }, [setAnchorEl]);
    
    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, [setAnchorEl]);
    
    const ValueComponent = SourceDataItemMapping[item?.itemMetadata.fieldType ?? SourceDataFieldType.Text];
    
    const statusText = noteStatusToText(itemProp?.status);
    
    let createdDate = undefined;
    let initialNote = itemProp.details.length > 0 ? itemProp.details[0] : undefined;
    if(initialNote) {
        createdDate = initialNote.createdDate ? DateTime.fromISO(initialNote.createdDate).toFormat('dd/MM/yyyy') : undefined
    }

    let latestUpdateDate = undefined;
    let lastUpdate = itemProp.details.length > 1 ? itemProp.details[itemProp.details.length-1] : undefined;
    if(lastUpdate) {
        latestUpdateDate = lastUpdate.createdDate ? DateTime.fromISO(lastUpdate.createdDate!).toFormat('dd/MM/yyyy') : undefined
    }
    
    const actionsOffsetPx = theme.spacing(2);
    const actionsOffset = useMemo(() => -(actionsOffsetPx.replace("px", "")), [actionsOffsetPx]);

    return (
        <>
            {
                !hideNoteType && 
                <TableCell sx={{width: columnDefinitions["Note Type"]}} >{noteTypeToText(itemProp.type)}</TableCell>
            }
            {
                !hideStatus &&
                <TableCell sx={{width: columnDefinitions["Resolution Status"]}}>
                    {
                        itemProp.status &&
                        <StatusListItemTooltip
                            title={
                                <>
                                    <strong>Status</strong><br />
                                    {statusText}
                                </>
                            }
                            followCursor
                        >
                            <StatusChip label={statusText} color={noteStatusToColor(itemProp.status)} />
                        </StatusListItemTooltip>
                    }
                </TableCell>
            }
            <TableCell sx={{width: columnDefinitions["Date Created"]}}>{createdDate}</TableCell>
            {
                !hideTrial &&
                <TableCell sx={{width: columnDefinitions["Trial"]}}><Tooltip title="Go to Trial" followCursor><Link to={`/${trial?.oid}`}>{trial?.name}</Link></Tooltip></TableCell>
            }
            {
                !hideInstitution && 
                <TableCell sx={{width: columnDefinitions["Institution"]}} ><Tooltip title="Go to Institution" followCursor><Link to={`/${trial?.oid}/institutions/${institution?.code}`}>{institution?.name}</Link></Tooltip></TableCell>
            }
            {
                !hideParticipant && 
                <TableCell sx={{width: columnDefinitions["Participant"]}} ><Tooltip title="Go to Participant" followCursor><Link to={`/${trial?.oid}/institutions/${institution?.code}/participants/${participant?.studyNumber}`}>{participant?.studyNumber}</Link></Tooltip></TableCell>
            }
            {
                !hideEvent && 
                <TableCell sx={{width: columnDefinitions["Study Event"]}} >
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                        <Tooltip title="Go to Study Event" followCursor><Link to={`/${trial?.oid}/institutions/${institution?.code}/participants/${participant?.studyNumber}/events/${studyEvent?.studyEventOid}/${itemProp?.studyEventRepeat}`}>{studyEvent?.displayName}</Link></Tooltip>
                        {
                            studyEvent?.eventMetadata.type === SourceDataEventType.Repeating && (
                                <StatusListItemTooltip 
                                    title="Event Repeat"
                                    followCursor
                                >
                                    <Chip 
                                        size="small" 
                                        icon={<FontAwesomeIcon icon={AppSettings.icons.studyEventRepeat} style={{fontSize: '1em'}} />} 
                                        label={itemProp.studyEventRepeat} 
                                    />
                                </StatusListItemTooltip>
                            )
                        }
                    </Box>
                </TableCell>
            }
            {
                !hideForm && 
                <TableCell sx={{width: columnDefinitions["Case Report Form"]}} ><Tooltip title="Go to Case Report Form" followCursor><Link to={`/${trial?.oid}/institutions/${institution?.code}/participants/${participant?.studyNumber}/events/${studyEvent?.studyEventOid}/${itemProp?.studyEventRepeat}/forms/${caseReportForm?.caseReportFormOid}`}>{caseReportForm?.displayName}</Link></Tooltip></TableCell>
            }
            {
                !hideItemGroup && 
                <TableCell sx={{width: columnDefinitions["Item Group"]}} >
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                        {itemGroup?.displayName}
                        {
                            itemGroup?.itemGroupMetadata?.type === SourceDataItemGroupType.Grid && (
                                <StatusListItemTooltip 
                                    title="Item Group Repeat"
                                    followCursor
                                >
                                    <Chip 
                                        size="small" 
                                        icon={<FontAwesomeIcon icon={AppSettings.icons.itemGroupRepeat} style={{fontSize: '1em'}} />} 
                                        label={itemProp.itemGroupRepeat} 
                                    />
                                </StatusListItemTooltip>
                            )
                        }
                    </Box>
                </TableCell>
            }
            {
                !hideItem && 
                <TableCell sx={{width: columnDefinitions["Item"]}} >{item?.displayName}</TableCell>
            }
            {
                !hideItemValue && 
                <TableCell sx={{width: columnDefinitions["Item Value"]}} ><ValueComponent itemData={item} hideBorderStyles/></TableCell>
            }
            <TableCell sx={{width: columnDefinitions["Description"]}} >{initialNote?.title}</TableCell>
            <TableCell sx={{width: columnDefinitions["Detailed Notes"], whiteSpace: "pre-wrap"}} >{initialNote?.details}</TableCell>
            <TableCell sx={{width: columnDefinitions["Latest Response"]}} >{lastUpdate?.title}</TableCell>
            <TableCell sx={{width: columnDefinitions["Latest Response Details"], whiteSpace: "pre-wrap"}} >{lastUpdate?.details}</TableCell>
            <TableCell sx={{width: columnDefinitions["Created By"]}}>{initialNote?.creatorUsername}</TableCell>
            <TableCell sx={{width: columnDefinitions["Updated By"]}}>{lastUpdate?.creatorUsername}</TableCell>
            <TableCell sx={{width: columnDefinitions["Date Updated"]}}>{latestUpdateDate}</TableCell>
            {
                !editStatus && showEdit && (
                    <TableCell sx={{width: columnDefinitions["Actions"]}}>
                        <Box
                            sx={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "center"
                            }}
                        >
                            <Button
                                color="primary"
                                variant="contained"
                                onClick={handleClick}
                                sx={{
                                    minWidth: "unset",
                                    padding: theme.spacing(1, 1.5)
                                }}
                            >
                                <FontAwesomeIcon icon={AppSettings.icons.combined} />
                            </Button>
                        </Box>
                        <Menu
                            open={open}
                            onClose={handleClose}
                            anchorEl={anchorEl}
                            transformOrigin={{
                                vertical: actionsOffset,
                                horizontal: 0
                            }}
                            MenuListProps={{
                                // This removes the top and bottom padding from the menu list
                                disablePadding: true, 
                            }}
                        >
                            <MenuItem
                                onClick={() => {
                                    handleClose();
                                    setView(true);
                                }}
                                
                            >
                                View Details
                            </MenuItem>
                            {
                                itemProp.status && AppSettings.noteActions[itemProp.status].resolutionProposed && (
                                    <MenuItem
                                        onClick={() => {
                                            handleClose();
                                            setEditStatus(NoteStatus.ResolutionProposed);
                                        }}
                                        
                                    >
                                        Resolution Proposed
                                    </MenuItem>
                                )
                            }
                            {
                                itemProp.status && AppSettings.noteActions[itemProp.status].addAdditionalInformation && (
                                    <MenuItem
                                        onClick={() => {
                                            handleClose();
                                            setEditStatus(itemProp.status);
                                        }}
                                    >
                                        Add Additional Information
                                    </MenuItem>
                                )
                            }
                            {
                                itemProp.status && AppSettings.noteActions[itemProp.status].reset && (
                                    <MenuItem
                                        onClick={() => {
                                            handleClose();
                                            setEditStatus(NoteStatus.New);
                                        }}
                                    >
                                        Reset
                                    </MenuItem>
                                )
                            }
                            {
                                itemProp.status && AppSettings.noteActions[itemProp.status].reopen && (
                                    <MenuItem
                                        onClick={() => {
                                            handleClose();
                                            setEditStatus(NoteStatus.New);
                                        }}
                                    >
                                        Reopen
                                    </MenuItem>
                                )
                            }
                            {
                                itemProp.status && AppSettings.noteActions[itemProp.status].close && (
                                    <MenuItem
                                        onClick={() => {
                                            handleClose();
                                            setEditStatus(NoteStatus.Closed);
                                        }}
                                    >
                                        Close
                                    </MenuItem>
                                )
                            }
                        </Menu>
                    </TableCell>
                )
            }
            {
                (!!editStatus || view) && (
                    <Dialog
                        open={true}
                        maxWidth={"lg"}
                        fullWidth
                    >
                        <MemoizedInnerListItem
                            index={index}
                            item={itemProp}
                            context={context}
                            informationOnly={true}
                        />
                        <Divider />
                        {
                            view ? 
                                (
                                    <Box
                                        sx={{ display: 'flex', justifyContent: 'flex-end', padding: theme.spacing(2) }}
                                    >
                                        <Button
                                            variant="contained"
                                            color="inherit"
                                            onClick={onEditCancel}
                                        >
                                            Close
                                        </Button>
                                    </Box>
                                ) :
                                (
                                    <NoteEditForm
                                        trialId={itemProp.trialId}
                                        masterGroupId={itemProp.masterGroupId}
                                        coordinatingGroupId={itemProp.coordinatingGroupId}
                                        institutionId={itemProp.institutionId}
                                        participantId={itemProp.participantId}
                                        studyEventOid={itemProp.studyEventOid}
                                        studyEventRepeat={itemProp.studyEventRepeat}
                                        caseReportFormOid={itemProp.caseReportFormOid}
                                        caseReportFormVersionOid={itemProp.caseReportFormVersionOid}
                                        itemGroupName={itemProp.itemGroupName}
                                        itemGroupRepeat={itemProp.itemGroupRepeat}
                                        itemName={itemProp.itemName}
                
                                        noteId={itemProp.id}
                                        status={editStatus}
                                        onCancel={onEditCancel}
                                    />
                                )
                        }
                    </Dialog>
                )
            }
        </>
    );
}

// Item contents are cached properly with React.memo
const InnerListItem =({ 
    index,
    item,
    context,
    informationOnly
}: IVirtuosoInnerItem) => {
    return (
        <NoteView
            {...context}
            data={item}
            informationOnly={informationOnly}
        />
    )
};

const MemoizedInnerListItem = memo(InnerListItem) as typeof InnerListItem;
const MemoizedNoteTableRow = memo(NoteTableRow) as typeof NoteTableRow;

// The callback is executed often - don't inline complex components in here.
const virtuosoItemContent = (index: number, item: Note, context: IVirtuosoContext) => {
    return <MemoizedInnerListItem index={index} item={item} context={context} informationOnly={false} />
}

interface INotesAndQueriesListProps {
    trialId?: number;
    masterGroupId?: number;
    coordinatingGroupId?: number;
    institutionId?: number;
    participantId?: number;
    studyEventOid?: string;
    studyEventRepeat?: number;
    caseReportFormOid?: string;
    caseReportFormVersionOid?: string;
    itemGroupName?: string;
    itemGroupRepeat?: number;
    itemName?: string;
    
    notesAndQueries?: Note[];

    canAdd?: boolean;
    hideDetails?: boolean;
}

interface INotesAndQueriesSearch extends Partial<Omit<Note, 'details' | typeof immerable>> {
    username?: string;
}

interface IDetailsProps {
    trialId?: number;
    masterGroupId?: number;
    coordinatingGroupId?: number;
    institutionId?: number;
    participantId?: number;
    studyEventOid?: string;
    studyEventRepeat?: number;
    caseReportFormOid?: string;
    caseReportFormVersionOid?: string;
    itemGroupName?: string;
    itemGroupRepeat?: number;
    itemName?: string;

    trials?: Record<number, Trial>;
    masterGroups?: Record<number, MasterGroup>;
    coordinatingGroups?: Record<number, CoordinatingGroup>;
    institutions?: Record<number, Institution>;
    participants?: Record<number, Participant>;
    metadata?: Metadata;
    trialConfiguration?: ConfigurationSourceDataMap;
    configuration?: ConfigurationSourceDataMap;
    sourceData?: SourceData;
}

const Details: FC<IDetailsProps> = ({
    trialId,
    masterGroupId,
    coordinatingGroupId,
    institutionId,
    participantId,
    studyEventOid,
    studyEventRepeat,
    caseReportFormOid,
    caseReportFormVersionOid,
    itemGroupName,
    itemGroupRepeat,
    itemName,

    trials,
    masterGroups,
    coordinatingGroups,
    institutions,
    participants,
    metadata,
    trialConfiguration,
    configuration,
    sourceData
}) => {
    return (
        <>
            <Subtitle variant="h2">
                Details
            </Subtitle>
            <Paper>
                <NoteDetails 
                    trialId={trialId}
                    masterGroupId={masterGroupId}
                    coordinatingGroupId={coordinatingGroupId}
                    institutionId={institutionId}
                    participantId={participantId}
                    studyEventOid={studyEventOid}
                    studyEventRepeat={studyEventRepeat}
                    caseReportFormOid={caseReportFormOid}
                    caseReportFormVersionOid={caseReportFormVersionOid}
                    itemGroupName={itemGroupName}
                    itemGroupRepeat={itemGroupRepeat}
                    itemName={itemName}

                    trials={trials}
                    masterGroups={masterGroups}
                    coordinatingGroups={coordinatingGroups}
                    institutions={institutions}
                    participants={participants}
                    metadata={metadata}
                    trialConfiguration={trialConfiguration}
                    configuration={configuration}
                    sourceData={sourceData}
                    
                    enableLinks={true}
                />
            </Paper>
        </>
    )
}

const NotesAndQueriesList: FC<INotesAndQueriesListProps> = ({
    trialId,
    masterGroupId,
    coordinatingGroupId,
    institutionId,
    participantId,
    studyEventOid,
    studyEventRepeat,
    caseReportFormOid,
    caseReportFormVersionOid,
    itemGroupName,
    itemGroupRepeat,
    itemName,

    notesAndQueries,

    canAdd,
    hideDetails
}) => {
    const theme = useTheme();
    const userContext = useContext(ExternalUsersContext);
    const { idMap: trials } = useContext(TrialsContext);
    const { idMap: masterGroups } = useContext(MasterGroupsContext);
    const { idMap: coordinatingGroups } = useContext(CoordinatingGroupsContext);
    const { idMap: institutions } = useContext(InstitutionsContext);
    const { idMap: participants } = useContext(ParticipantsContext);
    const { data: metadata } = useContext(MetadataContext);
    const { data: trialConfiguration } = useContext(TrialConfigurationSourceDataContext);
    const { data: configuration } = useContext(ConfigurationSourceDataContext);
    const { data: sourceData } = useContext(SourceDataContext);

    const [urlSearchParams, setUrlSearchParams] = useSearchParams();
    
    const [viewType, setViewType] = useLocalStorage<ListViewType>('notes-view-type', ListViewType.List);

    const searchData = useMemo(() => {
        const search: INotesAndQueriesSearch = {
            
        };

        if (!trialId && urlSearchParams.has('trialId')) {
            const query = urlSearchParams.get('trialId');

            search.trialId = query ? query as unknown as number : undefined;
        }

        if (!masterGroupId && urlSearchParams.has('masterGroupId')) {
            const query = urlSearchParams.get('masterGroupId');

            search.masterGroupId = query ? query as unknown as number : undefined;
        }

        if (!coordinatingGroupId && urlSearchParams.has('coordinatingGroupId')) {
            const query = urlSearchParams.get('coordinatingGroupId');

            search.coordinatingGroupId = query ? query as unknown as number : undefined;
        }

        if (!institutionId && urlSearchParams.has('institutionId')) {
            const query = urlSearchParams.get('institutionId');

            search.institutionId = query ? query as unknown as number : undefined;
        }

        if (!participantId && urlSearchParams.has('participantId')) {
            const query = urlSearchParams.get('participantId');

            search.participantId = query ? query as unknown as number : undefined;
        }

        if (!studyEventOid && urlSearchParams.has('studyEventOid')) {
            const query = urlSearchParams.get('studyEventOid');

            search.studyEventOid = query ? query : undefined;
        }

        if (!studyEventRepeat && urlSearchParams.has('studyEventRepeat')) {
            const query = urlSearchParams.get('studyEventRepeat');

            search.studyEventRepeat = query ? +query : undefined;
        }

        if (!caseReportFormOid && urlSearchParams.has('caseReportFormOid')) {
            const query = urlSearchParams.get('caseReportFormOid');

            search.caseReportFormOid = query ? query : undefined;
        }

        if (!caseReportFormVersionOid && urlSearchParams.has('caseReportFormVersionOid')) {
            const query = urlSearchParams.get('caseReportFormVersionOid');

            search.caseReportFormVersionOid = query ? query : undefined;
        }

        if (!itemGroupName && urlSearchParams.has('itemGroupName')) {
            const query = urlSearchParams.get('itemGroupName');

            search.masterGroupId = query ? +query : undefined;
        }

        if (!itemGroupRepeat && urlSearchParams.has('itemGroupRepeat')) {
            const query = urlSearchParams.get('itemGroupRepeat');

            search.itemGroupRepeat = query ? +query : undefined;
        }

        if (!itemName && urlSearchParams.has('itemName')) {
            const query = urlSearchParams.get('itemName');

            search.itemName = query ? query : undefined;
        }

        

        if (urlSearchParams.has('type')) {
            const query = urlSearchParams.get('type');

            search.type = query ? +query : undefined;
        }

        
        if (urlSearchParams.has('status')) {
            const query = urlSearchParams.get('status');

            search.status = query ? +query : undefined;
        }

        if(urlSearchParams.has('username')) {
            const user = urlSearchParams.get('username');

            search.username = user ?? undefined;
        }

        return search;
    }, 
    [
        urlSearchParams,
        trialId,
        masterGroupId,
        coordinatingGroupId,
        institutionId,
        participantId,
        studyEventOid,
        studyEventRepeat,
        caseReportFormOid,
        caseReportFormVersionOid,
        itemGroupName,
        itemGroupRepeat,
        itemName
    ])

    const virtuosoContext = useMemo(() => {
        return {
            theme,
            trialId: trialId ?? searchData.trialId,
            masterGroupsId: masterGroupId ?? searchData.masterGroupId,
            coordinatingGroupId: coordinatingGroupId ?? searchData.coordinatingGroupId,
            institutionId: institutionId ?? searchData.institutionId,
            participantId: participantId ?? searchData.participantId,
            studyEventOid: studyEventOid ?? searchData.studyEventOid,
            studyEventRepeat: studyEventRepeat ?? searchData.studyEventRepeat,
            caseReportFormOid: caseReportFormOid ?? searchData.caseReportFormOid,
            caseReportFormVersionOid: caseReportFormVersionOid ?? searchData.caseReportFormVersionOid,
            itemGroupName: itemGroupName ?? searchData.itemGroupName,
            itemGroupRepeat: itemGroupRepeat ?? searchData.itemGroupRepeat,
            itemName: itemName ?? searchData.itemName,

            trials: trials,
            masterGroups,
            coordinatingGroups,
            institutions,
            participants,
            metadata,
            trialConfiguration,
            configuration,
            sourceData,
        }
    }, [
        theme,
        trials,
        masterGroups,
        coordinatingGroups,
        institutions,
        participants,
        metadata,
        trialConfiguration,
        configuration,
        sourceData,
        trialId,
        masterGroupId,
        coordinatingGroupId,
        institutionId,
        participantId,
        searchData
    ]);

    // const searchableFields = useMemo(() => {
    //     const context = {
    //         trials,
    //         masterGroups,
    //         coordinatingGroups,
    //         institutions,
    //         participants
    //     }

    //     return [
    //         !trialId && trials ? DefaultSearchableFields.trial(context) : undefined,
    //         !masterGroupId && masterGroups ? DefaultSearchableFields.masterGroup(context) : undefined,
    //         !coordinatingGroupId && coordinatingGroups ? DefaultSearchableFields.coordinatingGroup(context) : undefined,
    //         !institutionId && institutions ? DefaultSearchableFields.institution(context) : undefined,
    //         !participantId && participants ? DefaultSearchableFields.participant(context) : undefined,

    //         DefaultSearchableFields.type(context),
    //         DefaultSearchableFields.status(context)
    //     ].filter(x => !!x) as ISearchField<any>[]
    // }, [
    //     trials,
    //     masterGroups,
    //     coordinatingGroups,
    //     institutions,
    //     participants,

    //     trialId,
    //     masterGroupId,
    //     coordinatingGroupId,
    //     institutionId,
    //     participantId
    // ]);

    const sortableFields = useMemo(() => {
        return [
            // !trialId && trials && !searchData.trialId ? DefaultSortableFields.trial : undefined,
            // !masterGroupId && masterGroups && !searchData.masterGroupId ? DefaultSortableFields.masterGroup : undefined,
            // !coordinatingGroupId && coordinatingGroups && !searchData.coordinatingGroupId ? DefaultSortableFields.coordinatingGroup : undefined,
            // !institutionId && institutions && !searchData.institutionId ? DefaultSortableFields.institution : undefined,
            // !participantId && participants && !searchData.participantId ? DefaultSortableFields.participant : undefined,

            DefaultSortableFields.type,
            DefaultSortableFields.status,
            DefaultSortableFields.dateCreated
        ].filter(x => !!x) as INotesAndQueriesListSortable[]
    }, [
        trials,
        masterGroups,
        coordinatingGroups,
        institutions,
        participants,

        trialId,
        masterGroupId,
        coordinatingGroupId,
        institutionId,
        participantId,
        searchData
    ]);

    // const [searchOpen, setSearchOpen] = useState(false);
    const [selectedSort, setSelectedSort] = useState<undefined | {field: INotesAndQueriesListSortable; direction: 'asc' | 'desc'}>(undefined);

    const onSortChange = useCallback((sort : {field: INotesAndQueriesListSortable; direction: 'asc' | 'desc'}) => {
        setSelectedSort(sort)
    }, [setSelectedSort])
    
    // const onSearchClose = useCallback(() => {
    //     setSearchOpen(false);
    // }, [setSearchOpen])

    // const onSearchOk = useCallback(async (data?: ISearchForm) => {
    //     const newSearch = {...data};

    //     if (newSearch.participantId) {
    //         const participant = participants?.[newSearch.participantId];

    //         newSearch.trialId = participant?.trialId;
    //         newSearch.masterGroupId = participant?.masterGroupId;
    //         newSearch.coordinatingGroupId = participant?.coordinatingGroupId;
    //         newSearch.institutionId = participant?.institutionId;
    //     }
    //     else if (newSearch.institutionId) {
    //         const institution = institutions?.[newSearch.institutionId];

    //         newSearch.trialId = institution?.trialId;
    //         newSearch.masterGroupId = institution?.masterGroupId;
    //         newSearch.coordinatingGroupId = institution?.coordinatingGroupId;
    //     }
    //     else if (newSearch.coordinatingGroupId) {
    //         const coordinatingGroup = coordinatingGroups?.[newSearch.coordinatingGroupId];

    //         newSearch.trialId = coordinatingGroup?.trialId;
    //         newSearch.masterGroupId = coordinatingGroup?.masterGroupId;
    //     }
    //     else if (newSearch.masterGroupId) {
    //         const masterGroup = masterGroups?.[newSearch.masterGroupId];

    //         newSearch.trialId = masterGroup?.trialId;
    //     }

    //     for (let key in newSearch) {
    //         if (newSearch[key] === undefined) {
    //             delete newSearch[key];
    //         }
    //     }

    //     setUrlSearchParams(newSearch as any)
    // }, [
    //     trials,
    //     masterGroups,
    //     coordinatingGroups,
    //     institutions,
    //     participants,

    //     trialId,
    //     masterGroupId,
    //     coordinatingGroupId,
    //     institutionId,
    //     participantId,
        
    //     setUrlSearchParams
    // ])


    // const filterCount = useMemo(() => {
    //     const searchedFields = searchableFields?.filter(x => !!searchData?.[x.key as keyof INotesAndQueriesSearch]) ?? [];

    //     return searchedFields.length;
    // }, [searchData, searchableFields]);


    const filteredUserContext = useMemo(() => {
        const distinctUsernames = notesAndQueries ? 
            Array.from(new Set(notesAndQueries?.flatMap(note => note.details?.map(detail => detail.creatorUsername)))) :
            [];

        return {
            ...userContext,
            data: userContext.data?.filter(user => distinctUsernames.includes(user.username)) ?? []
        }
    }, [notesAndQueries, userContext]);

    const sortedListItems = useMemo(() => {

        let newData = notesAndQueries?.filter(x => {
            for (let key in searchData) {
                if(key as keyof INotesAndQueriesSearch === "username" && filteredUserContext.data) {
                    const usernames = filteredUserContext.data.map(user => user.username);
                    const latestDetail = x.details.findLast(noteDetail => usernames.includes(noteDetail.creatorUsername));
                    if(latestDetail?.creatorUsername !== searchData.username) {
                        return false;
                    }
                }
                else if (
                    !!searchData[key as keyof INotesAndQueriesSearch] && 
                    searchData[key as keyof INotesAndQueriesSearch] != x[key as keyof Note]
                ) {
                    return false;
                }
            }

            return true;
        }) ?? [];

        if (!!selectedSort?.field  && !!selectedSort.direction && newData) {
            const context = {
                trials,
                masterGroups,
                coordinatingGroups,
                institutions,
                participants
            }

            newData = newData.sort((a,b) => selectedSort.field.sort(a,b,context) * (selectedSort.direction === 'desc' ? -1 : 1));
        }

        return newData;
    }, 
    [
        notesAndQueries, 
        searchData, 
        selectedSort,
        
        trials,
        masterGroups,
        coordinatingGroups,
        institutions,
        participants,

        filteredUserContext.data
    ]);

    const counts = useMemo(() => {
        const types = {
            [NoteType.Annotation]: 0,
            [NoteType.InternalQuery]: 0,
            [NoteType.ProtocolDeviation]: 0,
            [NoteType.ImportantProtocolDeviation]: 0,
            [NoteType.ReasonForChange]: 0,
            [NoteType.SiteQuery]: 0,
            [NoteType.System]: 0
        }

        const statuses = {
            [NoteStatus.New]: 0,
            [NoteStatus.Closed]: 0,
            [NoteStatus.NotApplicable]: 0,
            [NoteStatus.ResolutionProposed]: 0,
            [NoteStatus.Updated]: 0
        }

        let total = 0;

        if (notesAndQueries) {
            for (let note of notesAndQueries) {
                types[note.type!]++;
                statuses[note.status!]++;
                total++;
            }
        }

        return {
            types,
            statuses,
            total
        }
    }, [notesAndQueries])

    const location = useLocation();

    const hasSearch = true;
    const hasSort = true;

    const columnDefinitions = useColumnDefinitions();

    const [scroller, setScroller] = useState<HTMLElement | null>(null);

    const hideTrial = useMemo(() => !!trialId || !!searchData.trialId, [searchData.trialId, trialId]);
    const hideInstitution = useMemo(() => !!institutionId || !!searchData.institutionId, [searchData.institutionId, institutionId]);
    const hideParticipant = useMemo(() => !!participantId || !!searchData.participantId, [searchData.participantId, participantId]);
    const hideEvent = useMemo(() => !!studyEventOid || !!searchData.studyEventOid, [searchData.studyEventOid, studyEventOid]);
    const hideForm = useMemo(() => !!searchData.caseReportFormOid, [searchData.caseReportFormOid]);
    const hideItemGroup = useMemo(() => !!searchData.itemGroupName, [searchData.itemGroupName]);
    const hideItem = useMemo(() => !!searchData.itemName, [searchData.itemName]);
    const hideItemValue = useMemo(() => !!searchData.itemValue, [searchData.itemValue]);
    const hideNoteType = useMemo(() => !!searchData.type, [searchData.type]);
    const hideStatus = useMemo(() => !!searchData.status, [searchData.status]);
    const columnNames = useMemo(() => {
        const columnNames: string[] = [];
    
        if(!hideNoteType) {
            columnNames.push("Note Type");
        }

        if(!hideStatus) {
            columnNames.push("Resolution Status");
        }

        columnNames.push("Date Created");
    
        if(!hideTrial) {
            columnNames.push("Trial");
        }
    
        if(!hideInstitution) {
            columnNames.push("Institution");
        }
    
        if(!hideParticipant) {
            columnNames.push("Participant");
        }
    
        if(!hideEvent) {
            columnNames.push("Study Event");
        }
    
        if(!hideForm) {
            columnNames.push("Case Report Form");
        }
    
        if(!hideItemGroup) {
            columnNames.push("Item Group");
        }
    
        if(!hideItem) {
            columnNames.push("Item");
        }
    
        if(!hideItemValue) {
            columnNames.push("Item Value");
        }

        columnNames.push("Description");
        columnNames.push("Detailed Notes");
        columnNames.push("Latest Response");
        columnNames.push("Latest Response Details");
        columnNames.push("Created By");
        columnNames.push("Updated By");
        columnNames.push("Date Updated");
        columnNames.push("Actions")

        return columnNames;
    }, [hideTrial, hideInstitution, hideParticipant, hideEvent, hideForm, hideItemGroup, hideItem, hideItemValue, hideNoteType, hideStatus]);

    const totalWidth = useMemo(() => {
        return columnNames.reduce<number>((state, current) => state+columnDefinitions[current], 0)
    }, [columnNames, columnDefinitions]);

    const tableComponents = useMemo(() => ({
        Table: NoteTable,// (props: any) => <Table {...props} sx={{tableLayout: "fixed"}} />,
        TableHead: (props: any) => (
            <TableHead 
                {...props} 
                sx={{
                    backgroundColor: theme.palette.mode === "dark" ? "#121212" : theme.palette.common.white,
                    backgroundImage: "linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))"
                }}
            />
        ),
        TableRow: StyledTableRow
    }), [theme.palette.mode]);

    
    const virtuosoTableRow = useCallback((index: number, item: Note, context: IVirtuosoContext) => {
        return (
            <MemoizedNoteTableRow 
                index={index} 
                item={item} 
                context={context} 
                hideTrial={hideTrial}
                hideInstitution={hideInstitution}
                hideParticipant={hideParticipant}
                hideEvent={hideEvent}
                hideForm={hideForm}
                hideItemGroup={hideItemGroup}
                hideItem={hideItem}
                hideItemValue={hideItemValue}
                hideNoteType={hideNoteType}
                hideStatus={hideStatus}
                columnDefinitions={columnDefinitions}
            />
        );
    }, [columnDefinitions, hideTrial, hideInstitution, hideParticipant, hideEvent, hideForm, hideItemGroup, hideItem, hideItemValue, hideNoteType]);


    return (
        <ExternalUsersContext.Provider value={filteredUserContext}>
            {
                !hideDetails && (
                    <Details
                        trialId={trialId ?? searchData.trialId}
                        masterGroupId={masterGroupId ?? searchData.masterGroupId}
                        coordinatingGroupId={coordinatingGroupId ?? searchData.coordinatingGroupId}
                        institutionId={institutionId ?? searchData.institutionId}
                        participantId={participantId ?? searchData.participantId}
                        studyEventOid={studyEventOid ?? searchData.studyEventOid}
                        studyEventRepeat={studyEventRepeat ?? searchData.studyEventRepeat}
                        caseReportFormOid={caseReportFormOid ?? searchData.caseReportFormOid}
                        caseReportFormVersionOid={caseReportFormVersionOid ?? searchData.caseReportFormVersionOid}
                        itemGroupName={itemGroupName ?? searchData.itemGroupName}
                        itemGroupRepeat={itemGroupRepeat ?? searchData.itemGroupRepeat}
                        itemName={itemName ?? searchData.itemName}
        
                        trials={trials}
                        masterGroups={masterGroups}
                        coordinatingGroups={coordinatingGroups}
                        institutions={institutions}
                        participants={participants}
                        metadata={metadata}
                        trialConfiguration={trialConfiguration}
                        configuration={configuration}
                        sourceData={sourceData}
                    />
                )
            }      
            <Subtitle variant="h2">
                Notes
            </Subtitle>
            <FilterSortBox>
                <CountBox>
                    {sortedListItems.length} Items
                </CountBox>
                <SortBox>
                    <NotesAndQueriesListFilter
                        onChange={(type, status, username) => {
                            let data: any = {};

                            if (type) {
                                data.type = type;
                            }
                            
                            if (status) {
                                data.status = status;
                            }

                            if(username) {
                                data.username = username;
                            }

                            setUrlSearchParams(data)
                        }}
                        statuses={counts.statuses}
                        types={counts.types}
                        selectedStatus={searchData.status}
                        selectedType={searchData.type}
                        selectedUsername={searchData.username}
                    />
                </SortBox>
                {/* {
                    hasSearch && (
                        <SearchBox>
                            <Tooltip title="Filter">
                                <SearchButton onClick={() => setSearchOpen(true)} color={!!filterCount ? "primary" : "inherit"} variant="contained">
                                    <FontAwesomeIcon icon={AppSettings.icons.filter} size="lg" />
                                </SearchButton>
                            </Tooltip>
                        </SearchBox>
                    )
                } */}
                {
                    hasSort && (
                        <SortBox>
                            <NotesAndQueriesListSort 
                                sortableFields={sortableFields}
                                sortKey="notes-and-queries"
                                onChange={onSortChange}
                            />
                        </SortBox>
                    )
                }
                <ViewBox>
                        <ListTableToggle 
                            onChange={(val) => setViewType(val)}
                            value={viewType ?? ListViewType.List}
                        />
                </ViewBox>
                {
                    canAdd && AppSettings.allowNotesAndQueriesEdit && (
                        <SearchBox sx={{ml:2}}>
                            <Tooltip title="Add">
                                <SearchButton 
                                    variant="contained" 
                                    color="primary"
                                    //@ts-ignore
                                    component={Link}
                                    to={`${location.pathname}/add`}
                                >
                                    <FontAwesomeIcon icon={AppSettings.icons.add} size="lg" />
                                </SearchButton>
                            </Tooltip>
                        </SearchBox>
                    )
                }
            </FilterSortBox>

            {
                sortedListItems.length === 0 && (
                    <Alert
                        severity="warning"
                    >
                        <AlertTitle>No Notes or Queries</AlertTitle>
                        No notes or queries were found that match the requested criteria.
                    </Alert> 
                )
            }
            {
                sortedListItems.length !== 0 && viewType === ListViewType.Table && (
                    <Paper 
                        ref={(ref) => setScroller(ref as HTMLElement)}
                        sx={{
                            height: `calc(100vh - ${theme.spacing(14)})`,
                            overflow: "auto"
                        }}
                    >
                        <Box
                            sx={{
                                width: totalWidth
                            }}
                        >
                            {
                                scroller &&
                                sortedListItems.length > 0 &&
                                    <TableVirtuoso
                                        components={tableComponents}
                                        fixedHeaderContent={() => ( 
                                            <TableRow>
                                            {
                                                columnNames.map(cn => (
                                                    <TableCell key={`table-header-${cn.replace(" ", "-")}`} sx={{width: columnDefinitions[cn]}} >{cn}</TableCell>
                                                ))
                                            }
                                            </TableRow>
                                        )}
                                        useWindowScroll
                                        customScrollParent={scroller}
                                        data={sortedListItems}
                                        context={virtuosoContext}
                                        itemContent={virtuosoTableRow}
                                    />
                            }
                        </Box>
                    </Paper>
                )
            }
            {
                sortedListItems.length !== 0 && viewType === ListViewType.List && (
                    <List disablePadding>
                        {
                            sortedListItems.length > 20 && (
                                <Virtuoso 
                                    useWindowScroll
                                    data={sortedListItems}
                                    context={virtuosoContext}
                                    itemContent={virtuosoItemContent}
                                />  
                            )
                        }
                        {
                            sortedListItems.length <= 20 && (
                                sortedListItems.map((x, i) => (
                                    <MemoizedInnerListItem key={i} index={i} item={x} context={virtuosoContext} />
                                ))
                            )
                        }              
                    </List>
                )
            }
            {/* <SearchDialog 
                open={searchOpen}
                searchFields={searchableFields}
                formData={searchData}
                onSave={onSearchOk}
                onClose={onSearchClose}
            /> */}
        </ExternalUsersContext.Provider>
    )
}

export default NotesAndQueriesList;